import { FaIcon, ThemeTokens } from '@acp/core-ui';
import {
    Button,
    ButtonGroup,
    IconButton, Menu, MenuButton, MenuItem, MenuList, useDisclosure,
} from '@chakra-ui/react';
import { ComponentProps } from 'react';
import { faChevronDown, faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons';
import { Tenancy } from '@acp/shared';
import { EditTenancyModal } from './modals/edit-tenancy-modal';
import { MarkPropertyAsVacantModal } from '../../property/components/mark-property-as-vacant-modal';
import { ArchiveTenancyModal } from '../../property/components/archive-tenancy-modal';

export enum TenancyActionsMenuType {
    ICON = 'ICON',
    BUTTON = 'BUTTON'
}

function IconMenu({
    onEditTenancyModalOpen,
    onMarkPropertyAsVacantOpen,
    onArchiveTenancyOpen,
    showMarkPropertyAsVacantAction,
    showArchiveTenancyAction,
    ...props
}: {
    onEditTenancyModalOpen: () => void;
    onMarkPropertyAsVacantOpen: () => void;
    onArchiveTenancyOpen: () => void;
    showMarkPropertyAsVacantAction: boolean;
    showArchiveTenancyAction: boolean;
} & ComponentProps<typeof MenuButton>) {
    return (
        <Menu>
            <MenuButton
                as={IconButton}
                aria-label="Actions"
                icon={<FaIcon fontSize="lg" icon={faEllipsisVertical} />}
                variant="ghost"
                color="inherit"
                colorScheme="muted"
                _hover={{ bg: 'transparent' }}
                _active={{ bg: 'transparent' }}
                size="xs"
                {...props}
            />
            <MenuList>
                <MenuItem onClick={onEditTenancyModalOpen}>
                    Edit/Renew tenancy
                </MenuItem>

                {showMarkPropertyAsVacantAction && (
                    <MenuItem onClick={onMarkPropertyAsVacantOpen}>
                        Mark property as vacant
                    </MenuItem>
                )}

                {showArchiveTenancyAction && (
                    <MenuItem onClick={onArchiveTenancyOpen}>
                        Archive tenancy
                    </MenuItem>
                )}
            </MenuList>
        </Menu>
    );
}

function ButtonMenu({
    onEditTenancyModalOpen,
    onMarkPropertyAsVacantOpen,
    onArchiveTenancyOpen,
    showMarkPropertyAsVacantAction,
    showArchiveTenancyAction,
    ...props
}: {
    onEditTenancyModalOpen: () => void;
    onMarkPropertyAsVacantOpen: () => void;
    onArchiveTenancyOpen: () => void;
    showMarkPropertyAsVacantAction: boolean;
    showArchiveTenancyAction: boolean;
} & ComponentProps<typeof Button>) {
    return (
        <ButtonGroup
            size="sm"
            isAttached
            justifySelf="end"
        >
            <Button
                onClick={onEditTenancyModalOpen}
                borderRightWidth={1}
                borderRightColor="secondary.900"
                borderColor={ThemeTokens.Defaults.borderColor}
                color="muted.700"
                colorScheme="charcoal"
                variant="outline"
                {...props}
            >
                Edit tenancy
            </Button>
            <Menu size="lg">
                <MenuButton
                    as={IconButton}
                    borderStartRadius={0}
                    icon={<FaIcon icon={faChevronDown} />}
                    borderColor={ThemeTokens.Defaults.borderColor}
                    color="muted.700"
                    variant="outline"
                />
                <MenuList>
                    {showMarkPropertyAsVacantAction && (
                        <MenuItem onClick={onMarkPropertyAsVacantOpen}>
                            Mark property as vacant
                        </MenuItem>
                    )}

                    {showArchiveTenancyAction && (
                        <MenuItem onClick={onArchiveTenancyOpen}>
                            Archive tenancy
                        </MenuItem>
                    )}
                </MenuList>
            </Menu>
        </ButtonGroup>
    );
}

export function TenancyActions({
    tenancy,
    propertyId,
    showMenuAs = TenancyActionsMenuType.ICON,
    showMarkPropertyAsVacantAction,
    showArchiveTenancyAction = false,
    ...props
}: {
    tenancy: Tenancy;
    propertyId: number;
    showMenuAs?: TenancyActionsMenuType;
    showMarkPropertyAsVacantAction: boolean;
    showArchiveTenancyAction?: boolean;
} & ComponentProps<typeof MenuButton>) {
    const editTenancyModal = useDisclosure();
    const markPropertyAsVacantModal = useDisclosure();
    const archiveTenancyModal = useDisclosure();

    return (
        <>
            {showMenuAs === TenancyActionsMenuType.ICON && (
                <IconMenu
                    onEditTenancyModalOpen={editTenancyModal.onOpen}
                    onMarkPropertyAsVacantOpen={markPropertyAsVacantModal.onOpen}
                    onArchiveTenancyOpen={archiveTenancyModal.onOpen}
                    showMarkPropertyAsVacantAction={showMarkPropertyAsVacantAction}
                    showArchiveTenancyAction={showArchiveTenancyAction}
                    {...props}
                />
            )}

            {showMenuAs === TenancyActionsMenuType.BUTTON && (
                <ButtonMenu
                    onEditTenancyModalOpen={editTenancyModal.onOpen}
                    onMarkPropertyAsVacantOpen={markPropertyAsVacantModal.onOpen}
                    onArchiveTenancyOpen={archiveTenancyModal.onOpen}
                    showMarkPropertyAsVacantAction={showMarkPropertyAsVacantAction}
                    showArchiveTenancyAction={showArchiveTenancyAction}
                    {...props}
                />
            )}

            <EditTenancyModal
                tenancy={tenancy}
                propertyId={propertyId}
                isOpen={editTenancyModal.isOpen}
                onClose={editTenancyModal.onClose}
            />

            <MarkPropertyAsVacantModal
                propertyId={propertyId}
                isOpen={markPropertyAsVacantModal.isOpen}
                onClose={markPropertyAsVacantModal.onClose}
            />

            <ArchiveTenancyModal
                propertyId={propertyId}
                tenancyId={tenancy.tenancyId}
                isOpen={archiveTenancyModal.isOpen}
                onClose={archiveTenancyModal.onClose}
            />
        </>
    );
}
